import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, MessageIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createHostFamilyOlComment } from '../../../api/host-families/host-family-ol-comments/create-host-family-ol-comment'
import { deleteHostFamilyOlComments } from '../../../api/host-families/host-family-ol-comments/delete-host-family-ol-comment'
import { getHostFamilyOlComments } from '../../../api/host-families/host-family-ol-comments/get-host-family-ol-comments'
import { updateHostFamilyOlComment } from '../../../api/host-families/host-family-ol-comments/update-host-family-ol-comment'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { HostFamilyOlComment } from '../../../lib/types/models/host-family-ol-comment/host-family-ol-comment'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import ActionTableWrapper from '../../elements/action-table-wrapper'
import { mapHostFamilyOlCommentToInput } from '../ols/host-family/host-family-information/utils'
import HostFamilyOlCommentForm from './host-family-ol-comment-form'

export type Props = {
  id: number
}

const HostFamilyOlCommentTable = ({ id }: Props) => {
  const queryClient = useQueryClient()

  const { modalState, closeModel, selectedData, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    HostFamilyOlComment
  >(['create', 'edit', 'delete'])

  const { data: hostFamilyOlComments } = useQuery({
    queryKey: ['host-family-ol-comments'],
    queryFn: () => getHostFamilyOlComments({ hostFamilyId: id })
  })

  const deleteComment = useMutation({
    mutationFn: deleteHostFamilyOlComments,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment deleted successfully')
      queryClient.invalidateQueries({
        queryKey: ['host-family-ol-comments']
      })
      closeModel('delete')
    }
  })

  const createComment = useMutation({
    mutationFn: createHostFamilyOlComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment added successfully')
      queryClient.invalidateQueries({
        queryKey: ['host-family-ol-comments']
      })
      closeModel('create')
    }
  })

  const updateComment = useMutation({
    mutationFn: updateHostFamilyOlComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment updated successfully')
      queryClient.invalidateQueries({
        queryKey: ['host-family-ol-comments']
      })
      closeModel('edit')
    }
  })

  const columns: Column<HostFamilyOlComment>[] = [
    {
      enableSorting: true,
      header: 'Comments',
      accessorKey: 'comment',
      cell: ({ row }) => (
        <Typography
          variant="tag"
          color={tailwindColors.black.DEFAULT}
          className="text-wrap px-3 py-4"
        >
          {row.original.comment}
        </Typography>
      )
    },
    {
      enableSorting: true,
      header: 'Date & time',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <Badge
          text={`${DateFormatter.DDMMYYYY(row.original.updatedAt)} - ${DateFormatter.HHmm(row.original.updatedAt)}`}
          variant="grey"
        />
      )
    },
    {
      enableSorting: false,
      header: '',
      columnHeaderClassName: 'w-12',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2 pr-3'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original)
            }}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              toggleModal('delete', row.original)
            }}
          />
        </div>
      )
    }
  ]

  if (!hostFamilyOlComments) return <Loader center />

  return (
    <InnerBox className=" rounded-lg border border-grey-light p-0">
      <ActionTableWrapper
        title={'OL Comments'}
        transparent
        actions={[
          <Button
            key={'add'}
            type="button"
            icon={PlusIcon}
            label={'Add comment'}
            variant={'outline'}
            onClick={() => toggleModal('create')}
          />
        ]}
      >
        <DataTable<HostFamilyOlComment>
          manualSorting={false}
          columns={columns}
          data={hostFamilyOlComments.data ?? []}
          rowCount={hostFamilyOlComments?.data.length ?? 0}
          paginated={false}
        />

        <Modal
          isOpen={modalState.create}
          title={'Add a comment'}
          size="medium"
          hideActions
          icon={MessageIcon}
          onCancel={() => closeModel('create')}
        >
          <HostFamilyOlCommentForm
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('create'),
              onConfirm: async (values) => {
                const { updatedAt: _updatedAt, ...rest } = values
                await createComment.mutateAsync({ ...rest, hostFamilyId: id })
              },
              isEnglish: true
            })}
          />
        </Modal>

        {selectedData && (
          <Modal
            isOpen={modalState.edit}
            title={'Edit comment'}
            hideActions
            icon={MessageIcon}
            size="medium"
            onCancel={() => closeModel('edit')}
          >
            <HostFamilyOlCommentForm
              defaultValues={mapHostFamilyOlCommentToInput(selectedData)}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeModel('edit'),
                onConfirm: async (values) => {
                  const { updatedAt: _updatedAt, ...rest } = values
                  await updateComment.mutateAsync({
                    ...rest,
                    id: selectedData.id,
                    hostFamilyId: id
                  })
                },
                isEnglish: true
              })}
            />
          </Modal>
        )}

        {selectedData && (
          <Modal
            actionButtonsOptions={{
              confirmText: 'Delete',
              cancelText: 'Cancel',
              onConfirm: async () => {
                await deleteComment.mutateAsync({ hostFamilyId: id, ids: [selectedData.id] })
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={'Are you sure you want to delete this comment?'}
            icon={DeleteIcon}
            isLoading={deleteComment.isPending}
            variant={'destructive'}
          />
        )}
      </ActionTableWrapper>
    </InnerBox>
  )
}

export default HostFamilyOlCommentTable
